"use client";

import MediaImage from "@/components/ui/MediaImage";

import { headerMenuPresentation } from "@/utils/header-menu-presentation";

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import MenuItem from "../../MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleUser,
  faMagnifyingGlass,
} from "@fortawesome/sharp-light-svg-icons";
import {
  headerMotion,
  useStillHeaderMenu,
} from "@/components/motion/header-motion";
import { legacyMenuVariants } from "@/utils/legacy-menu-motion";
import { headerContentWidth } from "@/utils/visual-style";
import { HeaderProps } from "../models";
import SearchBar from "@/components/ui/SearchBar";
import { getPaddings, getTextSize } from "@/utils/theme-helper";
import { resolveNavigation } from "@/utils/header-navigation";
import { resolveHeaderActions } from "@/utils/header-actions";
import { headerSurfacesInDark } from "@/utils/header-colours";
import { menuHoverMarker } from "@/utils/header-navigation";
import HeaderMenu from "@/components/navigation/HeaderMenu";
import HeaderActions from "@/components/navigation/HeaderActions";
import HeaderBottomBar from "@/components/navigation/HeaderBottomBar";
import MenuActions from "@/components/navigation/MenuActions";
import { resolveHeaderLogos } from "@/utils/header-logos";
import { lookSchemeSettings, lookSwitchProps } from "@/utils/look-scheme";
import { withDarkLogo } from "@/utils/look-logo";
import LookSchemeSwitch from "@/components/ui/LookSchemeSwitch";
import { useHeaderState } from "../HeaderStateController";
import { isLookSystem } from "@/utils/look-gate";

const DefaultHeader: React.FC<HeaderProps> = ({
  cmsData,
  appData,
  header,
  pageHeader,
  logoDefinition,
}) => {
  const logoUrl = appData?.logo?.originalUrl;
  const logos = resolveHeaderLogos({
    website: cmsData ?? {},
    definition: logoDefinition,
    config: { ...header, type: "DefaultHeader" },
    media: { logo: logoUrl },
  });
  const headerRef = useRef<HTMLElement>(null);
  const [showMenu, setShowMenu] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);
  const view = useHeaderState();
  const shownTop = view?.settings.top;
  const lookSystem = isLookSystem(cmsData?.theme);
  const { motion, AnimatePresence } = headerMotion(lookSystem);
  const stillMenu = useStillHeaderMenu(lookSystem);
  const menuMotion = legacyMenuVariants(stillMenu);
  const managed = !!view;
  const shown = view ? !view.frame.hidden : isVisible;

  const handleScroll = useCallback(() => {
    if (!window) {
      return;
    }

    if (window.scrollY > lastScrollY && window.scrollY > 1) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(window.scrollY);
  }, [lastScrollY]);

  useEffect(() => {
    if (!window || managed) {
      return;
    }

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, managed]);

  const menuItems = cmsData?.pages
    .filter((x) => x.includeInMenu && x.type !== "article")
    .map((page) => ({
      title: page.menuTitle,
      href: page.slug,
      redirectToExternal: page.redirectToExternal,
      openInNewTab: page.openInNewTab,
      externalUrl: page.externalUrl,
    }));

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const toggleSearch = () => {
    if (navigation) setShowMenu(false);
    setShowSearch(!showSearch);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showSearch) {
        setShowSearch(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSearch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showMenu) {
        setShowMenu(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMenu]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setShowSearch(false);
        setShowMenu(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  let customerPortalUrl =
    cmsData?.b2cLink ?? appData?.resourceGroup?.primaryB2CApplication?.url;
  if (customerPortalUrl && !customerPortalUrl.startsWith("https://")) {
    customerPortalUrl = "https://" + customerPortalUrl.replace("http://", "");
  }

  const heightMobile = header.heightMobile || "80px";
  const heightDesktop = header.heightDesktop || "100px";
  const menuIconName = cmsData?.subdomain;
  const isSecondaryLayout = cmsData?.layout?.name === "secondary";
  const navigation = useMemo(
    () =>
      resolveNavigation({
        site: cmsData,
        pages: cmsData?.pages ?? [],
        type: header.type,
        root: cmsData?.layout?.header,
        page: pageHeader,
        colours: logoDefinition?.colours,
        top: shownTop,
      }),
    [cmsData, header.type, pageHeader, logoDefinition, shownTop],
  );
  const { bar: barInDark, panel: panelInDark } = headerSurfacesInDark(
    cmsData?.theme,
    {
      type: header.type,
      config: cmsData?.layout?.header,
      page: pageHeader,
      top: shownTop,
    },
    logoDefinition?.colours,
  );
  const actions = useMemo(
    () =>
      Array.isArray(header.actions)
        ? resolveHeaderActions(header.actions, {
            pages: cmsData?.pages ?? [],
            portalUrl: customerPortalUrl,
            theme: cmsData?.theme,
            surfaces: { bar: barInDark, panel: panelInDark },
          })
        : undefined,
    [header.actions, cmsData, customerPortalUrl, barInDark, panelInDark],
  );
  const headerSwitch = lookSwitchProps(cmsData, "header");
  const menuSwitch = lookSwitchProps(cmsData, "menu");
  const actionIcons = {
    search: (
      <FontAwesomeIcon className="size-[24px] flex" icon={faMagnifyingGlass} />
    ),
    user: <FontAwesomeIcon className="size-[24px] flex" icon={faCircleUser} />,
  };

  const logo = (
    <Link
      aria-label="Home"
      className="flex group-data-[centerlogo=true]:md:col-start-2"
      href={"/"}
    >
      {(logos.primary.source === "header" || cmsData?.logoUrl || logoUrl) &&
        withDarkLogo(
          <MediaImage
            src={logos.primary.url}
            alt="logo"
            data-re-logo={managed ? "" : undefined}
            height={0}
            width={150}
            style={
              {
                "--heightMobile": heightMobile,
                "--heightDesktop": heightDesktop,
              } as React.CSSProperties
            }
            className={`py-4 h-[var(--heightMobile)] lg:h-[var(--heightDesktop)] w-auto  ${cmsData?.layout?.header?.paddingDefault ? getPaddings(cmsData?.layout?.header?.paddingDefault) : ""}`}
          />,
          lookSchemeSettings(cmsData?.theme)?.logo,
        )}
    </Link>
  );
  const searchButton = (
    <button
      aria-label="Search"
      className="p-[8px] flex "
      onClick={() => toggleSearch()}
    >
      <FontAwesomeIcon className="size-[24px] flex" icon={faMagnifyingGlass} />
    </button>
  );
  const customerLink = customerPortalUrl && (
    <Link
      aria-label="Kundeunivers"
      target="_blank"
      href={customerPortalUrl}
      className="p-[8px] flex"
    >
      <FontAwesomeIcon className="size-[24px] flex" icon={faCircleUser} />
    </Link>
  );

  return (
    <div id="DefaultHeader">
      <div
        id="layover"
        className={`fixed inset-0 bg-gray-800 z-30 transition-opacity duration-300 ${
          showMenu && !navigation
            ? "opacity-40"
            : "opacity-0 pointer-events-none"
        }`}
      ></div>
      <header
        ref={headerRef}
        data-re-menu-open={(navigation && showMenu) || undefined}
        data-re-menu-style={navigation?.options.style}
        data-re-header={managed ? "standard" : undefined}
        style={
          {
            "--heightMobile": heightMobile,
            "--heightDesktop": heightDesktop,
          } as React.CSSProperties
        }
        className={`h-[var(--heightMobile)] text-[var(--header-text-color)] md:h-[var(--heightDesktop)] flex justify-between bg-[var(--header-bg-color)] fixed top-0 left-0 right-0 z-30 transition-transform duration-300 ${
          shown ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <AnimatePresence>
          {showMenu && !navigation && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              className={`flex text-[var(--menu-text-color)] h-dvh fixed top-0 z-20 right-0 left-0 lg:left-auto bottom-0 lg:w-[380px] pt-28 pl-[30px]`}
            >
              {/* Animating clipPath itself restores the open circle for one frame before the closed menu is removed; a custom property keeps the last frame. */}
              <motion.div
                className="bg-[var(--menu-bg-color)] absolute inset-0 z-20"
                style={{
                  clipPath:
                    "circle(var(--re-menu-circle, 0%) at calc(100% - 54px) 50px)",
                }}
                variants={menuMotion.circle}
              ></motion.div>
              <motion.div
                variants={menuMotion.list}
                className={`flex flex-col gap-4 relative  z-20 flex-1 overflow-y-auto pb-10 ${getTextSize(cmsData?.layout?.header?.menuTextSize, "lg")} ${isSecondaryLayout ? "primary-font" : ""}`}
              >
                {menuItems?.map((item, index) => (
                  <motion.div key={`${index}-menu`} variants={menuMotion.item}>
                    <MenuItem
                      key={item.href}
                      href={item.href}
                      onClick={() => setShowMenu(false)}
                      redirectToExternal={item.redirectToExternal}
                      openInNewTab={item.openInNewTab}
                      externalUrl={item.externalUrl}
                      customIconName={menuIconName}
                    >
                      {item.title}
                    </MenuItem>
                  </motion.div>
                ))}
                {actions && (
                  <MenuActions
                    actions={actions}
                    bp="lg"
                    icons={actionIcons}
                    onSearch={toggleSearch}
                    onNavigate={() => setShowMenu(false)}
                  />
                )}
                {menuSwitch && <LookSchemeSwitch {...menuSwitch} />}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {navigation ? (
          <HeaderMenu
            lookSystem={lookSystem}
            open={showMenu}
            onOpenChange={setShowMenu}
            presentation={{ ...headerMenuPresentation(header), solid: true }}
            inheritedTextClassName={`${getTextSize(cmsData?.layout?.header?.menuTextSize, "lg")} ${isSecondaryLayout ? "primary-font" : ""}`}
            navigation={navigation}
            hoverMarker={menuHoverMarker(menuIconName)}
            actions={actions}
            legacyActions={
              <>
                {searchButton}
                {customerLink}
              </>
            }
            logo={logo}
            logoPosition={
              header.logoPlacement === "center" ? "center" : "start"
            }
            rowClassName="re-nav-row"
            rowStyle={headerContentWidth(header.maxWidth)}
            bp="lg"
            appearance="default"
            anchor={headerRef}
            onSearch={toggleSearch}
            icons={actionIcons}
            schemeSwitch={
              headerSwitch && <LookSchemeSwitch {...headerSwitch} />
            }
            panelExtras={menuSwitch && <LookSchemeSwitch {...menuSwitch} />}
          />
        ) : (
          <div
            data-centerlogo={header.logoPlacement === "center"}
            style={headerContentWidth(header.maxWidth)}
            className={`px-[20px] lg:px-[40px] flex-1 grid grid-cols-2 data-[centerlogo=true]:md:grid-cols-[1fr_auto_1fr] bg-[var(--header-bg-color)] items-center group`}
          >
            {logo}
            <div className="flex gap-4 lg:gap-[34px] items-center justify-end">
              {actions ? (
                <HeaderActions
                  actions={actions}
                  bp="lg"
                  icons={actionIcons}
                  onSearch={toggleSearch}
                />
              ) : (
                <>
                  {searchButton}
                  {customerLink}
                </>
              )}
              {headerSwitch && <LookSchemeSwitch {...headerSwitch} />}
              <button
                aria-label="Menu"
                onClick={() => (showSearch ? toggleSearch() : toggleMenu())}
                className="z-[60] size-10 relative focus:outline-none rounded group"
              >
                <div className="block w-7 absolute left-6 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span
                    className={`block absolute right-0 h-0.5  group-hover:w-full bg-current transition-all transform duration-300 ease-in-out
                    ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                     ${
                       showMenu || showSearch
                         ? "rotate-45 w-full "
                         : "-translate-y-2 w-[70%]"
                     }`}
                  ></span>
                  <span
                    className={`block absolute right-0 h-0.5  group-hover:w-full bg-current  transition-all  transform duration-300 ease-in-out ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                                        ${
                                          showMenu || showSearch
                                            ? "opacity-0"
                                            : "w-[60%]"
                                        }`}
                  ></span>
                  <span
                    className={`block absolute right-0 h-0.5 w-full bg-current transform  transition duration-300 ease-in-out ${showMenu ? "text-[var(--menu-text-color)]" : "text-[var(--header-text-color)]"}
                   ${showMenu || showSearch ? "-rotate-45" : "translate-y-2"}`}
                  ></span>
                </div>
              </button>
            </div>
          </div>
        )}
      </header>
      {actions && (
        <HeaderBottomBar
          actions={actions}
          bp="lg"
          icons={actionIcons}
          onSearch={toggleSearch}
        />
      )}
      <SearchBar
        cmsData={cmsData!}
        toggleSearch={() => toggleSearch()}
        showSearch={showSearch}
      />
    </div>
  );
};

export default DefaultHeader;
